// Help text for: Tools > Off-Grid Calculator.
// Keys are referenced as "offGrid.<field>" by <FieldHelp id=... />. Entry shape:
//   title  - field name as shown on screen
//   use    - what the field is for / how to read it
//   source - measured | calculated | backend | default | setting | input
//   how    - where the value comes from: formula, default value, or who sets it
const help = {
  analysisPeriod: {
    title: "Past year of meter data",
    use: "The stretch of real usage the sizing is based on, so the answer covers your busiest day and your longest night.",
    source: "default",
    how: "Fixed at the 365 days ending yesterday, using hour-by-hour readings from the breakers. Solar, generator and calculated meters are left out because they are not load. You cannot change this period here.",
  },
  branch: {
    title: "Branch",
    use: "Picks which branch's machines get sized. Only shown when the branch picker at the top of the screen is set to All.",
    source: "input",
    how: "Starts on the first branch in your list. Choosing a branch at the top of the screen overrides this box.",
  },
  combinedSizing: {
    title: "Combined sizing",
    use: "One solar system big enough to run every machine hanging off the main you choose (Main 1 or Main 2).",
    source: "calculated",
    how: "Every machine under that main is sized from its own past year of breaker readings, then the sizes are added up. Starts on Main 1. Each machine's worst day and hardest pull are added in even though they happened at different times, so the answer errs on the large side.",
  },
  customSizing: {
    title: "Custom sizing",
    use: "One solar system sized for only the machines you tick in the Select machines list.",
    source: "calculated",
    how: "Same working as Combined sizing, applied to your picks: each machine is sized from its own past year of breaker readings and the sizes are added up.",
  },
  scope: {
    title: "Sizing scope",
    use: "Shows what the result below covers: the branch, the main, and how many machines went into it.",
    source: "backend",
    how: "The machine count is how many machines sit under the chosen main in your site layout, not counting solar, generator and calculated meters.",
  },
  selectMachines: {
    title: "Select machines",
    use: "Tick the machines to include in a Custom sizing, then press Analyze.",
    source: "input",
    how: "The list shows every machine in the branch from your site layout, with solar, generator and calculated meters left out. The small tag says whether it is a main incomer (Main) or an ordinary machine (Machine). Nothing is ticked to start with.",
  },
  recommendedSystem: {
    title: "Recommended Solar System",
    use: "The panel, battery and inverter sizes you would need to run the chosen load with no grid at all.",
    source: "calculated",
    how: "Worked out from the past year of hourly breaker readings. It always sizes for the worst month for sunshine (December, 4.1 peak sun hours) so the system copes all year round. The three assumptions — 75% solar efficiency, 90% usable battery, 25% inverter headroom — are fixed and cannot be changed here.",
  },
  solarPanels: {
    title: "Solar panels",
    use: "Total panel capacity needed to make as much energy as your heaviest day used.",
    source: "calculated",
    how: "Highest single-day energy use in the past year (kWh) ÷ (4.1 peak sun hours × 0.75 for system losses), shown in kWp. 4.1 hours is December, the lowest of the built-in monthly sun hours, which run from 4.1 to 5.8 hours.",
  },
  battery: {
    title: "Battery",
    use: "The battery storage needed to carry the load right through the night with no grid.",
    source: "calculated",
    how: "The most energy used on any single night (6 PM to 6 AM) in the past year (kWh) ÷ 0.90, because only about 90% of a battery can safely be used. Shown in kWh.",
  },
  autonomy: {
    title: "Battery backup wanted",
    use: "How many hours the battery should carry your load with no solar and no grid. Drag it to see the battery size and cost change.",
    source: "input",
    how: "Starts at 12 hours, the full night (6 PM to 6 AM) the calculator sizes for. Other values scale your worst night's average load: battery kWh = that average kW × hours ÷ 0.90. Above 12 hours it assumes the same average keeps going. Solar panels and inverter do not change.",
  },
  capexRoi: {
    title: "Estimated CAPEX & ROI",
    use: "A rough cost for the recommended system and how many years of avoided grid bills it would take to pay that back.",
    source: "calculated",
    how: "CAPEX = solar kWp × your solar rate + battery kWh × your battery rate + inverter kW × your inverter rate. Yearly saving = the machines' energy over the past year (scaled to 12 months if the meters are newer) × your grid rate. Payback = CAPEX ÷ yearly saving. The rates start as placeholders (the solar rate is a mid-range price for panels only, without mounting, cabling or labour); use the gear to enter supplier quotes. Maintenance, battery replacement, financing and tariff changes are not included.",
  },
  sizingRationale: {
    title: "Why this size?",
    use: "The real day in your records that the solar array was sized from, so you can see the recommendation rests on measured data.",
    source: "measured",
    how: "The single day in the past year when the selected machines together used the most energy, shown hour by hour. The array is sized from each machine's own worst day, so for several machines it can exceed what any one real day needed; the note under the headline says by how much.",
  },
  selectionBudget: {
    title: "Total peak load",
    use: "The combined peak of the machines you have ticked, so you can drop non-essential ones until the system fits your budget.",
    source: "measured",
    how: "Each machine's highest hourly reading in the past year, added together. This is the same figure the inverter is sized from, before its 25% headroom. Machines rarely all peak at once, so the true simultaneous peak is lower.",
  },
  inverter: {
    title: "Inverter",
    use: "The inverter rating needed to feed the load at its hardest pull.",
    source: "calculated",
    how: "Highest hourly power reading in the past year (kW) × 1.25 for headroom. With several machines, each machine's own peak is added in, even though they did not all peak at the same moment.",
  },
};

export default help;
